import initSearch from './js/algolia/search-algolia';

/**
 * Load external script
 * @param {string} src Script URL
 * @return {Promise<void>}
 */
const loadScript = ( src ) => {

	return new Promise( ( resolve ) => {

		const s = document.createElement( 'script' );
		s.src = src;
		s.async = true;
		s.onload = resolve;
		document.head.appendChild( s );

	} );

};

// Load Algolia libraries on demand
let __algoliaLibsLoaded = false;

/**
 *
 */
const loadAlgoliaLibraries = async () => {

	if ( __algoliaLibsLoaded ) {

		return;

	}

	await loadScript( 'https://cdn.jsdelivr.net/npm/algoliasearch@4.24.0/dist/algoliasearch.umd.min.js' );
	await loadScript( 'https://cdn.jsdelivr.net/npm/instantsearch.js@4.72.0/dist/instantsearch.production.min.js' );
	await loadScript( 'https://cdn.jsdelivr.net/npm/@algolia/autocomplete-js@1.17.7/dist/umd/index.production.js' );

	__algoliaLibsLoaded = true;

};

document.addEventListener( 'DOMContentLoaded', () => {

	const searchToggle = document.querySelector( '.js-searchToggle' );
	let isSearchInitialized = false;
	let searchInstance = null;

	// scroll vars
	let scrollY = 0;
	const body = document.body;

	const searchCloseBtn = document.querySelector( '.opt-search__bar-close' );

	/**
	 * Initialize search if not already initialized
	 * @return {void}
	 */
	const ensureSearchInitialized = async () => {

		if ( !isSearchInitialized ) {

			await loadAlgoliaLibraries();

			searchInstance = initSearch();
			isSearchInitialized = true;

		}

	};

	/**
	 * Toggle search UI state
	 * @param {boolean} shouldOpen - Whether to open or close the search
	 */
	const toggleSearchUI = ( shouldOpen ) => {

		const searchBar = document.querySelector( '.js-searchBar' );
		const searchOverlay = document.querySelector( '.js-searchOverlay' );
		const isSearchPage = new URLSearchParams( window.location.search ).has( 's' );

		/**
		 * Waits until the search input is available in the DOM.
		 *
		 * Uses requestAnimationFrame to poll the DOM until
		 * `.opt-search__input` is rendered.
		 *
		 * @return {Promise<HTMLInputElement>} Resolves with the search input element
		 */
		const waitForInput = () => {

			return new Promise( resolve => {

				/**
				 *
				 */
				const check = () => {

					const input = document.querySelector( '.opt-search__input' );
					if ( input ) {

						resolve( input );

					}
					else {

						requestAnimationFrame( check );

					}

				};
				check();

			} );

		};

		if ( shouldOpen ) {

			searchBar.classList.add( 'is-active' );
			searchOverlay.classList.add( 'is-active' );
			searchToggle.setAttribute( 'aria-expanded', 'true' );

			waitForInput().then( ( searchInput ) => {

				searchInput.focus();

			} );

			body.classList.add( 'search-is-open' );

			// lock scroll
			scrollY = window.scrollY;
			body.style.top = `-${scrollY}px`;
			if( scrollY > 0 ) {

				body.classList.add( 'search-not-at-top' );

			}
			body.style.width = '100%';
			body.style.height = '100%';
			body.style.overflowY = 'hidden';
			document.documentElement.classList.add( 'no-scroll' );

		} else {

			searchBar.classList.remove( 'is-active' );
			searchOverlay.classList.remove( 'is-active' );
			searchToggle.setAttribute( 'aria-expanded', 'false' );

			waitForInput().then( ( searchInput ) => {

				if ( !isSearchPage ) {

					searchInput.value = '';

				}

			} );

			body.classList.add( 'search-is-open' );

			// unlock scroll
			body.style.top = '';
			body.classList.remove( 'search-not-at-top' );
			body.style.width = '';
			body.style.height = '';
			body.style.overflowY = '';
			document.documentElement.classList.remove( 'no-scroll' );
			window.scrollTo( 0, scrollY );

		}

	};

	// Initialize immediately if we're on the search results page
	const urlParams = new URLSearchParams( window.location.search );
	if ( urlParams.has( 's' ) ) {

		ensureSearchInitialized();

	}

	/**
	 * Close search if open
	 * @return {void}
	 */
	const closeSearch = () => {

		const isExpanded =
			searchToggle?.getAttribute( 'aria-expanded' ) === 'true';

		if ( !isExpanded ) {

			return;

		}

		toggleSearchUI( false );

	};


	if ( searchCloseBtn ) {

		searchCloseBtn.addEventListener( 'click', closeSearch );

	}

	// Handle search toggle click
	if ( searchToggle ) {

		searchToggle.addEventListener( 'click', () => {

			ensureSearchInitialized();
			const isExpanded = searchToggle.getAttribute( 'aria-expanded' ) === 'true';
			toggleSearchUI( !isExpanded );

		} );

		// Close search on ESC
		document.addEventListener( 'keydown', ( event ) => {

			if ( event.key === 'Escape' ) {

				closeSearch();

			}

		} );

		// Handle overlay click for closing
		const searchOverlay = document.querySelector( '.js-searchOverlay' );
		if ( searchOverlay ) {

			searchOverlay.addEventListener( 'click', () => {

				toggleSearchUI( false );

			} );

		}

	}

} );
